/**
 * Push Notification Service using Service Worker and Push API
 * Provides PWA-compatible push notifications for Dream AI
 */

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class PushNotificationService {
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
  private pushSubscription: PushSubscription | null = null;

  /**
   * Initialize the push notification service
   */
  async initialize(): Promise<boolean> {
    console.log('🚀 Initializing Push Notification Service...');

    if (!this.isSupported()) {
      console.warn('❌ Push notifications not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      this.serviceWorkerRegistration = await navigator.serviceWorker.register(
        '/sw.js',
        {
          scope: '/',
        },
      );

      console.log(
        '✅ Service Worker registered:',
        this.serviceWorkerRegistration,
      );

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Set up message listener for service worker communication
      navigator.serviceWorker.addEventListener(
        'message',
        this.handleServiceWorkerMessage,
      );

      return true;
    } catch (error) {
      console.error('💥 Failed to initialize push notifications:', error);
      return false;
    }
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Get current push notification permission status
   */
  async getPermissionStatus(): Promise<PermissionState> {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'notifications',
      });

      return permissionStatus.state;
    } catch (error) {
      console.warn('Could not query push permission');
      return 'prompt';
    }
  }

  /**
   * Check if we have an active push subscription (more accurate than just permission)
   */
  async hasActivePushSubscription(): Promise<boolean> {
    if (!this.serviceWorkerRegistration) {
      return false;
    }

    try {
      const subscription =
        await this.serviceWorkerRegistration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.warn('Could not check push subscription:', error);
      return false;
    }
  }

  /**
   * Check if push notification permission is granted
   */
  async isPermissionGranted(): Promise<boolean> {
    const status = await this.getPermissionStatus();
    return status === 'granted';
  }

  /**
   * Check if we're fully set up for push notifications (permission + subscription)
   */
  async isFullySetup(): Promise<boolean> {
    const hasPermission = await this.isPermissionGranted();
    const hasSubscription = await this.hasActivePushSubscription();
    return hasPermission && hasSubscription;
  }

  /**
   * Request push notification permission
   */
  async requestPermission(): Promise<boolean> {
    console.log('🔐 Requesting push notification permission...');

    if (!this.isSupported()) {
      console.warn('❌ Push notifications not supported');
      return false;
    }

    try {
      // Request notification permission (this is still needed for push notifications)
      const permission = await Notification.requestPermission();
      console.log('📋 Permission result:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('💥 Error requesting permission:', error);
      return false;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribeToPush(
    vapidPublicKey: string,
  ): Promise<PushSubscriptionData | null> {
    console.log('📝 Subscribing to push notifications...');

    if (!this.serviceWorkerRegistration) {
      console.error('❌ Service Worker not registered');
      return null;
    }

    if (!(await this.isPermissionGranted())) {
      console.error('❌ Notification permission not granted');
      return null;
    }

    try {
      // Check for existing subscription
      this.pushSubscription =
        await this.serviceWorkerRegistration.pushManager.getSubscription();

      if (this.pushSubscription) {
        console.log('✅ Using existing push subscription');
      } else {
        // Create new subscription
        this.pushSubscription =
          await this.serviceWorkerRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey),
          });
        console.log('✅ Created new push subscription');
      }

      // Convert subscription to serializable format
      const subscriptionData: PushSubscriptionData = {
        endpoint: this.pushSubscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(
            this.pushSubscription.getKey('p256dh')!,
          ),
          auth: this.arrayBufferToBase64(this.pushSubscription.getKey('auth')!),
        },
      };

      console.log('📋 Push subscription data:', subscriptionData);
      return subscriptionData;
    } catch (error) {
      console.error('💥 Error subscribing to push:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromPush(): Promise<boolean> {
    console.log('🚫 Unsubscribing from push notifications...');

    if (!this.pushSubscription) {
      console.log('ℹ️ No active push subscription');
      return true;
    }

    try {
      const success = await this.pushSubscription.unsubscribe();
      if (success) {
        this.pushSubscription = null;
        console.log('✅ Successfully unsubscribed from push');
      }
      return success;
    } catch (error) {
      console.error('💥 Error unsubscribing from push:', error);
      return false;
    }
  }

  /**
   * Handle messages from service worker
   */
  private handleServiceWorkerMessage = (event: MessageEvent) => {
    console.log('💬 Message from Service Worker:', event.data);

    if (event.data?.type === 'NAVIGATE_TO_THREAD') {
      const { threadId } = event.data;
      console.log('🧭 Navigating to thread:', threadId);
      // TODO: Implement thread navigation
      // This could dispatch a custom event or use a router
      window.dispatchEvent(
        new CustomEvent('navigate-to-thread', { detail: { threadId } }),
      );
    }
  };

  /**
   * Convert VAPID key from URL-safe base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Get current push subscription
   */
  getCurrentSubscription(): PushSubscription | null {
    return this.pushSubscription;
  }

  /**
   * Check if currently subscribed to push
   */
  get isSubscribed(): boolean {
    return this.pushSubscription !== null;
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();
